import Modal from 'antd/lib/modal/Modal';
import CloseIcon from 'assets/icons/CloseIcon';
import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import { platformStates } from 'redux/platformSlice';
import useUserDetail from 'hooks/useUserDetail';
import { Col, Row } from 'antd';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import mfaApi from 'api/mfaApi';
import { showSuccessModal } from 'components/Modals/CommonModals';
import { selectIsShowMfaModal, setIsShowMfaModal } from 'features/home/homeSlice';
import { isValidMfa } from './WithdrawModal';
import useThemes from 'hooks/useThemes';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'redux/userSlice';

interface MfaModalProps {}

const MfaModal: React.FunctionComponent<MfaModalProps> = (props) => {
  const { currentTheme } = useThemes();
  const { isPhone } = useAppSelector(platformStates);
  const t = useAppTranslation();
  const handleResponseError = useHandleResponseError();
  const dispatch = useAppDispatch();
  const isShow = useAppSelector(selectIsShowMfaModal);
  const { getUser } = useUserDetail();
  const [mfaOtp, setMfaOtp] = useState<string>('');

  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const currentUser = useSelector(selectCurrentUser);

  const getMfa = async () => {
    const { ok, body, error } = await mfaApi.get();
    if (ok && body) {
      setQrUrl(body.qrUrl);
      setSecret(body.secret);
    }
    if (error && error.message !== 'error.validate.mfa.is-enable') {
      const onOk = () => {
        handleCloseModal();
      };
      handleResponseError(error, onOk);
    }
  };

  const onSubmit = async () => {
    const { ok, error } = currentUser?.isEnableMfa
      ? await mfaApi.disable({ mfa: mfaOtp })
      : await mfaApi.create({ mfa: mfaOtp });
    if (ok) {
      const onOk = () => {
        getUser();
        handleCloseModal();
      };

      showSuccessModal({
        title: currentUser?.isEnableMfa ? t('mfa.disable.success.title') : t('mfa.success.title'),
        content: currentUser?.isEnableMfa
          ? t('mfa.disable.success.message')
          : t('mfa.success.message'),
        onOk,
      });
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const onMfaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^(?!.*\..*\..*)(?=.*\d)(?!.*\.\d{3,})(?!.{11,}$)[\d.]{0,10}$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidMfa(value)) || value === '') {
      setMfaOtp(value);
    }
  };

  const handleCloseModal = () => {
    setQrUrl(null);
    setMfaOtp('');
    dispatch(setIsShowMfaModal(false));
  };

  useEffect(() => {
    isShow && getMfa();
  }, [isShow]);

  return (
    <Modal
      className="common-modal deposit-modal"
      title={t('mfaModal.title')}
      visible={isShow}
      onCancel={handleCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
      zIndex={1100}
    >
      <div className={`center-content-col gap-3 ${isPhone ? 'px-10' : 'px-50'}`}>
        {currentUser?.isEnableMfa ? (
          <div className="w-full flex justify-center items-center gap-3">
            <div className="relative w-full mfa__modal">
              <Row gutter={[8, 8]} className="mfa__modal-row">
                <Col span={24} className="mfa__modal-content mfa__modal-disable">
                  <span>{t('mfaModal.disable-content')}</span>
                </Col>
              </Row>
              <Row gutter={[8, 8]} className="mfa__modal-row">
                <Col span={24} className="mfa__modal-content mfa__modal-disable">
                  <div className="common-input input-amount">
                    <input
                      type="text"
                      name="mfa"
                      id="mfa"
                      autoComplete="off"
                      onChange={onMfaChange}
                      value={mfaOtp}
                      placeholder={t('placeholder.enterMFA')}
                    />
                  </div>
                </Col>
              </Row>
              <Row gutter={[8, 8]} className="mfa__modal-row">
                <Col span={24} className="mfa__modal-content mfa__modal-disable">
                  <button type="submit" className="btn-submit" onClick={onSubmit}>
                    {t('mfaModal.disable')}
                  </button>
                </Col>
              </Row>
            </div>
          </div>
        ) : (
          <div className="w-full flex justify-center items-center gap-3">
            {qrUrl && (
              <div className="relative w-full mfa__modal">
                <Row gutter={[8, 8]} className="mfa__modal-row">
                  <Col span={2}>
                    <div className="mfa__modal-step">1</div>
                  </Col>
                  <Col span={22} className="mfa__modal-content">
                    <span>{t('mfaModal.content')}</span>
                  </Col>
                </Row>
                <Row gutter={[8, 8]} className="mfa__modal-row">
                  <Col span={2}>
                    <div className="mfa__modal-step">2</div>
                  </Col>
                  <Col span={22} className="mfa__modal-content">
                    <div className="mfa-qr_container">
                      <img src={qrUrl} alt="mfa-qr" />
                    </div>
                    <p>
                      <span>{`${t('mfaModal.secret')}:`}</span> {secret}
                    </p>
                  </Col>
                </Row>
                <Row gutter={[8, 8]} className="mfa__modal-row">
                  <Col span={2}>
                    <div className="mfa__modal-step">3</div>
                  </Col>
                  <Col span={22} className="mfa__modal-content">
                    <div className="common-input input-amount">
                      <input
                        type="text"
                        name="mfa"
                        id="mfa"
                        autoComplete="off"
                        onChange={onMfaChange}
                        value={mfaOtp}
                        placeholder={t('placeholder.enterMFA')}
                      />
                    </div>
                  </Col>
                </Row>
                <Row gutter={[8, 8]} className="mfa__modal-row">
                  <Col span={2}></Col>
                  <Col span={22} className="mfa__modal-content ">
                    <button type="submit" className="btn-submit" onClick={onSubmit}>
                      {t('mfaModal.create')}
                    </button>
                  </Col>
                </Row>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MfaModal;
